import { type CSSProperties } from "react";

interface RecoveryDialogProps {
  onRestore: () => void;
  onDiscard: () => void;
}

export default function RecoveryDialog({ onRestore, onDiscard }: RecoveryDialogProps) {
  return (
    <div style={overlayStyle} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="recovery-title"
        aria-describedby="recovery-description"
        style={dialogStyle}
        data-testid="recovery-dialog"
      >
        <div style={titleBarStyle}>
          <h2 id="recovery-title" style={titleStyle}>
            Restore unsaved changes?
          </h2>
        </div>
        <div style={bodyStyle}>
          <p id="recovery-description" style={messageStyle}>
            This browser has unsaved changes for the drawing. Restore them, or discard the backup
            and keep the cloud version.
          </p>
          <div style={buttonRowStyle}>
            <button type="button" onClick={onDiscard} style={recoveryDangerButtonStyle}>
              Discard backup
            </button>
            <button type="button" onClick={onRestore} style={primaryButtonStyle} autoFocus>
              Restore changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const MODAL_SHADOW = "var(--exms-shadow-modal)";

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10003,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: CSSProperties = {
  width: "100%",
  maxWidth: 440,
  background: "var(--island-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 12,
  boxShadow: MODAL_SHADOW,
  fontFamily: EXCALIDRAW_FONT,
  overflow: "hidden",
};

const titleBarStyle: CSSProperties = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--dialog-border-color)",
};

const titleStyle: CSSProperties = { margin: 0, fontSize: 14, fontWeight: 700 };

const bodyStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
};

const messageStyle: CSSProperties = { margin: 0, fontSize: 14, lineHeight: 1.5 };

const buttonRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 8,
};

const primaryButtonStyle: CSSProperties = {
  borderRadius: 8,
  border: "1px solid var(--color-primary)",
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "0.875rem",
  fontFamily: "inherit",
  fontWeight: 600,
  letterSpacing: "0.4px",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
};

const recoveryDangerButtonStyle: CSSProperties = {
  ...primaryButtonStyle,
  border: "1px solid var(--color-danger)",
  background: "var(--color-danger)",
};
